import { useState } from "react";
import { useSession } from "./SessionProvider";
import { useToast } from "@/hooks/use-toast";
import { X, Send, Search } from "lucide-react";
import { type Friend } from "@/lib/friends";

function HexAvatar({ letter, hue, size = 40 }: { letter: string; hue: number; size?: number }) {
  return (
    <div className="relative shrink-0" style={{ width: size, height: size, filter: "drop-shadow(0 0 6px hsl(142 100% 50% / 0.35))" }}>
      <div
        aria-hidden
        className="v2-hex-frame absolute inset-0"
        style={{ background: "linear-gradient(180deg, hsl(142 100% 56%), hsl(142 70% 30%))" }}
      />
      <div
        aria-hidden
        className="v2-hex-frame absolute"
        style={{ inset: 1.5, background: `hsl(${hue} 60% 22%)` }}
      />
      <div
        className="relative w-full h-full flex items-center justify-center font-extrabold"
        style={{ color: "var(--text-ink-bright)", fontSize: size * 0.42 }}
      >
        {letter}
      </div>
    </div>
  );
}

export function ShareToFriendModal() {
  const { shareTarget, closeShare } = useSession();
  const { toast } = useToast();
  const [note, setNote] = useState("");
  const [query, setQuery] = useState("");

  if (!shareTarget) return null;

  // b19: pending live source (see b19_backlog.md B19-2 PR #3+)
  const filtered = ([] as Friend[]).filter((f) =>
    f.handle.toLowerCase().includes(query.toLowerCase()) ||
    f.displayName.toLowerCase().includes(query.toLowerCase())
  );

  function send(friend: Friend) {
    toast({
      title: `Sent to ${friend.handle}`,
      description: "They'll get a notification.",
    });
    closeShare();
    setNote("");
    setQuery("");
  }

  return (
    <div
      role="dialog"
      aria-label="Share with a friend"
      className="fixed inset-0 z-[75] flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm anim-fade-up"
      onClick={closeShare}
    >
      <div
        className="w-full md:max-w-md v2-neon-card rounded-t-3xl md:rounded-3xl p-5 max-h-[90dvh] overflow-y-auto scrollbar-thin relative"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--surface-dark-7)" }}
      >
        <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none rounded-t-3xl md:rounded-3xl">
          <div className="v2-aurora v2-aurora--green" style={{ width: "26vmax", height: "26vmax", top: "-10vmax", right: "-10vmax", opacity: 0.3 }} />
        </div>

        <div className="relative">
          <div className="flex items-start justify-between mb-3">
            <div>
              <span className="v2-eyebrow"><span className="v2-eyebrow__dot" />SHARE</span>
              <h3 className="v2-display text-lg tracking-tight mt-2" style={{ color: "var(--text-ink-bright)" }}>Send to a friend</h3>
              <p className="text-xs mt-0.5" style={{ color: "hsl(142 18% 72%)" }}>
                Sharing: <span style={{ color: "var(--text-ink-bright)" }}>{shareTarget.label}</span>
              </p>
            </div>
            <button
              onClick={closeShare}
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: "var(--surface-dark-9)", border: "1px solid hsl(142 80% 35% / 0.5)", color: "var(--text-ink-bright-faint)" }}
              data-testid="button-close-share"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div
            className="mb-3 flex items-center gap-2 rounded-full px-3 py-2"
            style={{ background: "var(--surface-dark-9)", border: "1px solid hsl(142 80% 35% / 0.45)" }}
          >
            <Search className="w-3.5 h-3.5" style={{ color: "hsl(142 100% 60%)" }} />
            <input
              type="text"
              placeholder="Search friends"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm"
              style={{ color: "var(--text-ink-bright)" }}
              data-testid="input-share-search"
            />
          </div>

          <div className="space-y-1.5 max-h-72 overflow-y-auto scrollbar-thin">
            {filtered.map((f) => (
              <button
                key={f.id}
                data-testid={`share-friend-${f.id}`}
                onClick={() => send(f)}
                className="w-full v2-neon-card-soft p-3 flex items-center gap-3 text-left hover:brightness-110 transition"
              >
                <HexAvatar letter={f.monogram} hue={f.hue} size={42} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold leading-tight" style={{ color: "var(--text-ink-bright)" }}>{f.handle}</p>
                  <p className="text-[11px]" style={{ color: "hsl(142 18% 72%)" }}>{f.lastActive}</p>
                </div>
                <Send className="w-4 h-4 shrink-0" style={{ color: "hsl(142 100% 60%)" }} />
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-center text-xs py-6" style={{ color: "hsl(142 18% 72%)" }}>No friends match.</p>
            )}
          </div>

          <div className="mt-3">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note (optional)"
              rows={2}
              data-testid="input-share-note"
              className="w-full rounded-xl px-3 py-2 text-sm outline-none resize-none"
              style={{
                background: "var(--surface-dark-9)",
                border: "1px solid hsl(142 80% 35% / 0.45)",
                color: "var(--text-ink-bright)",
              }}
            />
          </div>

          <button
            onClick={closeShare}
            data-testid="button-share-cancel"
            className="w-full mt-3 rounded-full py-2.5 text-sm font-semibold"
            style={{
              background: "var(--surface-dark-9)",
              border: "1px solid hsl(142 80% 35% / 0.5)",
              color: "var(--text-ink-bright-faint)",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
